import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';
import { useMe } from '../../auth/MeContext';
import { useTenantContext } from '../../auth/useTenantContext';
import { useDisableUser, useEnableUser, type AdminUser } from '../../api/adminUsers';

interface Props {
  user: AdminUser;
  /** true when disabling this user would leave the tenant with no active admin. */
  isLastTenantAdmin: boolean;
  onClose: () => void;
}

/**
 * "Stato" tab of the user edit-accordion (master-detail). Surfaces the
 * disable/re-enable action that was previously only available in the orphaned
 * UserActionMenu. Disabling blocks self-disable, warns on the last tenant-admin,
 * and renders the backend's `operator_has_active_missions` guard (422) as a
 * friendly message.
 */
export function UserStatusTab({ user, isLastTenantAdmin, onClose }: Props) {
  const { t } = useTranslation(['user', 'common']);
  const { token, tenantCode } = useTenantContext();
  const { state } = useMe();
  const myUserId = state.kind === 'ok' ? state.me.user.id : null;

  const disableMut = useDisableUser(token, tenantCode);
  const enableMut = useEnableUser(token, tenantCode);
  const [error, setError] = useState<string | null>(null);

  const isSelf = myUserId === user.id;
  const isDisabled = user.status === 'disabled';
  const userLabel = user.display_name ?? user.email ?? t('user:page.fallbackLabel', { id: user.id });
  const busy = disableMut.isPending || enableMut.isPending;

  const onDisable = async () => {
    if (isSelf) return;
    setError(null);
    const base = t('user:actionMenu.confirmDisable', { label: userLabel });
    const msg = isLastTenantAdmin ? t('user:actionMenu.confirmDisableLastAdmin', { base }) : base;
    if (!window.confirm(msg)) return;
    try {
      await disableMut.mutateAsync(user.id);
    } catch (err: unknown) {
      const e = err as { body?: { code?: string; detail?: string; active_missions?: number }; message?: string };
      if (e?.body?.code === 'operator_has_active_missions') {
        setError(t('user:statusTab.activeMissionsError', { count: e.body.active_missions ?? 0 }));
      } else {
        setError(e?.body?.detail ?? e?.message ?? t('user:statusTab.errorGeneric'));
      }
    }
  };

  const onEnable = async () => {
    setError(null);
    try {
      await enableMut.mutateAsync(user.id);
    } catch (err: unknown) {
      const e = err as { body?: { detail?: string }; message?: string };
      setError(e?.body?.detail ?? e?.message ?? t('user:statusTab.errorGeneric'));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <span className="text-muted small">{t('user:statusTab.currentStatus')}: </span>
        <span className={`fw-medium ${isDisabled ? 'text-danger' : 'text-success'}`}>
          {t(`user:status.${user.status}`, { defaultValue: user.status })}
        </span>
      </div>
      <p className="small text-muted mb-0">
        {isDisabled ? t('user:statusTab.enableHint') : t('user:statusTab.disableHint')}
      </p>
      {error && <p role="alert" className="text-danger small mb-0">{error}</p>}
      <div className="d-flex justify-content-end gap-2">
        <Button type="button" color="light" size="sm" onClick={onClose}>{t('common:actions.close')}</Button>
        {isDisabled ? (
          <Button type="button" color="success" size="sm" disabled={busy} onClick={onEnable}>
            {t('user:actionMenu.enable')}
          </Button>
        ) : (
          <Button
            type="button" color="danger" size="sm"
            disabled={busy || isSelf}
            title={isSelf ? t('user:actionMenu.cannotDisableSelf') : undefined}
            onClick={onDisable}
          >
            {t('user:actionMenu.disable')}
          </Button>
        )}
      </div>
    </div>
  );
}
